import { Navigate, useLocation } from "react-router-dom";
import { AUTH_REDIRECT, LOGIN } from "@/config/path";

/**
 * Where a signed-out visitor was going, so signing in lands them THERE rather
 * than on the dashboard (owner, 2026-10-28: a live link opened in another
 * browser asks for the username and password, then shows the page).
 *
 * sessionStorage, not localStorage: it belongs to the tab the link was opened
 * in, and it survives the trip to the marketing site's sign-in and back
 * through /auth-redirect in that same tab.
 */
const RETURN_KEY = "xale_return_to";

/** Only an in-app path: no other origin ("//x"), no sign-in page loop. */
function isReturnPath(path: string): boolean {
  if (!path.startsWith("/") || path.startsWith("//")) return false;
  const pathname = path.split(/[?#]/)[0];
  return pathname !== "/" && pathname !== LOGIN && pathname !== AUTH_REDIRECT;
}

export function rememberReturnTo(path: string) {
  try {
    if (isReturnPath(path)) sessionStorage.setItem(RETURN_KEY, path);
  } catch {
    // Storage blocked — the visitor simply lands on the dashboard as before.
  }
}

/** The remembered path (once — it is cleared), or null. */
export function takeReturnTo(): string | null {
  try {
    const path = sessionStorage.getItem(RETURN_KEY);
    sessionStorage.removeItem(RETURN_KEY);
    return path && isReturnPath(path) ? path : null;
  } catch {
    return null;
  }
}

/** `<Navigate to={LOGIN}>` that remembers the page the visitor asked for. */
export function RedirectToLogin() {
  const { pathname, search, hash } = useLocation();
  rememberReturnTo(`${pathname}${search}${hash}`);
  return <Navigate to={LOGIN} replace />;
}
